import { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';

import {
  useCerrarTicket,
  useCerrarTicketConArchivos,
} from '@/shared/actions/erp/tickets-soporte-cliente';
import { useFetchArbolCierre } from '@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-catalogos.actions';
import FileDropzoneField from '@/shared/components/ui/FileDropzoneField';
import { ITicket } from '@/shared/interfaces/erp/tickets-soporte-clientes';
import { urlBackendSoftnet } from '@/shared';
import { erpApi } from '@/shared/axios';
import { toast } from 'react-toastify';
const { post } = erpApi();

type ModalCerrarProps = {
  open: boolean;
  onClose: () => void;
  ticketId: string;
  /** UUID del area destino del ticket. Filtra el arbol de cierre por esa area. */
  areaDestinoId?: string | null;
  ticket?: ITicket | null;
};

type FormData = {
  solucion: string;
  cierre_categoria: string;
  cierre_subcategoria: string;
  ipv4_publica: string;
};

export default function ModalCerrar({ open, onClose, ticketId, areaDestinoId, ticket }: ModalCerrarProps) {
  const [archivos, setArchivos] = useState<File[]>([]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: { solucion: '', cierre_categoria: '', cierre_subcategoria: '', ipv4_publica: '' },
  });

  // Arbol de cierre (V1_19_0) filtrado por area destino del ticket.
  // El backend aplica smart fallback: si el area no tiene propios, devuelve
  // las categorias/subcategorias genericas (default).
  const { data: arbolData, isLoading: isLoadingArbol } = useFetchArbolCierre(areaDestinoId);
  const arbol: any[] = (arbolData as any)?.data ?? [];

  const cerrarTicket = useCerrarTicket();
  const cerrarConArchivos = useCerrarTicketConArchivos();

  // Cuando cambia la categoria de cierre, limpiamos la subcategoria para
  // forzar al usuario a elegir una valida del nuevo set.
  const categoriaSeleccionada = watch('cierre_categoria');
  useEffect(() => {
    setValue('cierre_subcategoria', '');
  }, [categoriaSeleccionada, setValue]);

  const subcategoriasDisponibles = useMemo(() => {
    const cat = arbol.find((c: any) => c.id_cierre_categoria === categoriaSeleccionada);
    return cat?.subcategorias ?? [];
  }, [arbol, categoriaSeleccionada]);

  const handleCloseAll = () => {
    reset();
    setArchivos([]);
    onClose();
  };

  const esIPv4Valida = (ip: string): boolean => {
    const partes = ip.split('.');
    if (partes.length !== 4) return false;
    return partes.every(parte => {
      if (!/^\d{1,3}$/.test(parte)) return false;
      const numero = Number(parte);
      return numero >= 0 && numero <= 255;
    });
  };

  const [enviandoSoftnet, setEnviandoSoftnet] = useState(false);
  const onSubmit = async (data: FormData) => {

    if (ticket?.subcategoria_data?.nombre?.toUpperCase() === "SOLICITAR IP PUBLICA" || ticket?.subcategoria_data?.nombre?.toUpperCase() === "CANCELAR IP PUBLICA") {
      if (ticket?.subcategoria_data?.nombre?.toUpperCase() === "SOLICITAR IP PUBLICA" && !esIPv4Valida(data?.ipv4_publica)) {
        toast.error("Ingrese una direccion ip valida");
        return false;
      }
      // Si es ip publica, actualizar en softnet el parametro, 
      // que posterior migra a tickets cuando se detecte el cambio.
      const payloadSoftnet: any = [{
        idContrato: ticket?.contrato_data?.numero_referencia,
        ip_publica: ticket?.subcategoria_data?.nombre?.toUpperCase() === "CANCELAR IP PUBLICA" ? "" : data?.ipv4_publica,
        tipo_accion: ticket?.subcategoria_data?.nombre?.toUpperCase() === "CANCELAR IP PUBLICA" ? "Cancelar Publica" : "Solicitar Publica",
      }];

      setEnviandoSoftnet(true);
      const resp = await post<any[]>(
        `${urlBackendSoftnet}/api/update-datos-contrato`,
        payloadSoftnet,
        false
      ) as any;

      if (resp?.statusCode != 200) {
        setEnviandoSoftnet(false);
        toast.error("Ocurrio un error con el servicio externo. " + resp?.message || "");
        return false;
      }
      setEnviandoSoftnet(false);
    }

    const payload = {
      solucion: data.solucion,
      cierre_categoria: data.cierre_categoria,
      cierre_subcategoria: data.cierre_subcategoria,
    };

    if (archivos.length > 0) {
      cerrarConArchivos.mutate(
        {
          id: ticketId,
          solucion: data.solucion,
          cierreCategoria: data.cierre_categoria,
          cierreSubcategoria: data.cierre_subcategoria,
          archivos,
        },
        { onSuccess: handleCloseAll },
      );
    } else {
      cerrarTicket.mutate(
        { id: ticketId, data: payload },
        { onSuccess: handleCloseAll },
      );
    }

  };

  const enviando = cerrarTicket.isPending || cerrarConArchivos.isPending || enviandoSoftnet;

  useEffect(() => {
    if (ticket?.subcategoria_data?.nombre?.toUpperCase() === "CANCELAR IP PUBLICA") {
      setValue('ipv4_publica', ticket?.contrato_data?.ipv4_publica ?? '', { shouldValidate: true });
    } else {
      setValue('ipv4_publica', '');
    }
  }, [ticket, setValue]);

  return (
    <Dialog open={open} onClose={handleCloseAll} maxWidth="sm" fullWidth>
      <DialogTitle>Cerrar Ticket</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            {...register('solucion', { required: 'La solucion es requerida' })}
            label="Solucion"
            multiline
            minRows={4}
            fullWidth
            size="small"
            error={!!errors.solucion}
            helperText={errors.solucion?.message}
          />

          <Controller
            name="cierre_categoria"
            control={control}
            rules={{ required: 'La categoria de cierre es requerida' }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Categoria de Cierre"
                fullWidth
                size="small"
                error={!!errors.cierre_categoria}
                helperText={errors.cierre_categoria?.message}
                disabled={isLoadingArbol}
              >
                {arbol.map((cat: any) => (
                  <MenuItem key={cat.id_cierre_categoria} value={cat.id_cierre_categoria}>
                    {cat.nombre}{cat.es_generica ? ' (default)' : ''}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name="cierre_subcategoria"
            control={control}
            rules={{ required: 'La subcategoria de cierre es requerida' }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Subcategoria de Cierre"
                fullWidth
                size="small"
                error={!!errors.cierre_subcategoria}
                helperText={
                  errors.cierre_subcategoria?.message ||
                  (!categoriaSeleccionada ? 'Selecciona primero una categoria' : '')
                }
                disabled={!categoriaSeleccionada || subcategoriasDisponibles.length === 0}
              >
                {subcategoriasDisponibles.map((sub: any) => (
                  <MenuItem key={sub.id_cierre_subcategoria} value={sub.id_cierre_subcategoria}>
                    {sub.nombre}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          {(
            ticket?.subcategoria_data?.nombre?.toUpperCase() === "SOLICITAR IP PUBLICA" ||
            ticket?.subcategoria_data?.nombre?.toUpperCase() === "CANCELAR IP PUBLICA"
          ) && (
              <TextField
                {...register('ipv4_publica', { required: 'La IP pública es requerida' })}
                label="IP Pública asignada"
                fullWidth
                size="small"
                error={!!errors.ipv4_publica}
                helperText={errors.ipv4_publica?.message}
                disabled={
                  ticket?.subcategoria_data?.nombre?.toUpperCase() === "CANCELAR IP PUBLICA"
                }
              />
            )}

          <FileDropzoneField
            value={archivos}
            onChange={setArchivos}
            helperText="Adjunte evidencias del cierre (opcional). Imágenes, PDF, ZIP, Excel — máx. 50 MB c/u"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseAll} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="error"
          disabled={enviando}
        >
          {archivos.length > 0
            ? `Cerrar Ticket (${archivos.length} adjunto${archivos.length > 1 ? 's' : ''})`
            : 'Cerrar Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
